<template>
  <div class="list-toolbar">
    <div class="toolbar-top">
      <div>
        <h2 class="list-title">Supervisor Timesheets</h2>
        <p class="list-subtitle">
          Review and approve submitted timesheets from supervisors
        </p>
      </div>
      <div class="toolbar-actions">
        <b-button variant="secondary" size="sm" @click="$emit('refresh')">
          <b-icon-arrow-clockwise /> Refresh
        </b-button>
        <b-button
          v-if="canDownloadTimesheet"
          variant="primary"
          size="sm"
          :disabled="exportDisabled"
          @click="$emit('export')"
        >
          <b-icon-download /> Export
        </b-button>
      </div>
    </div>

    <div class="toolbar-row">
      <div class="filter-field filter-field-date">
        <label class="filter-label">From</label>
        <b-form-datepicker
          :value="dateFrom"
          locale="en-AU"
          :date-format-options="dateFormatOptions"
          size="sm"
          today-button
          reset-button
          close-button
          show-decade-nav
          class="date-input"
          @input="onFromInput"
        />
      </div>

      <div class="filter-field filter-field-date">
        <label class="filter-label">To</label>
        <b-form-datepicker
          :value="dateTo"
          locale="en-AU"
          :date-format-options="dateFormatOptions"
          size="sm"
          today-button
          reset-button
          close-button
          show-decade-nav
          class="date-input"
          @input="$emit('update:dateTo', $event)"
        />
      </div>

      <div class="filter-field">
        <label class="filter-label">Project</label>
        <b-form-select
          :value="selectedProjectId"
          :options="projectOptions"
          size="sm"
          class="filter-control filter-control-project"
          @input="$emit('update:selectedProjectId', $event)"
        />
      </div>

      <div class="filter-field">
        <label class="filter-label">Pay period</label>
        <div class="week-nav">
          <b-button
            variant="secondary"
            size="sm"
            class="week-nav-btn"
            aria-label="Current pay period"
            title="Current pay period"
            @click="$emit('apply-current-week')"
          >
            <b-icon-calendar3 />
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="week-nav-btn"
            aria-label="Previous pay period"
            @click="$emit('shift-week', -1)"
          >
            <b-icon-chevron-left />
          </b-button>
          <span class="week-nav-label">{{ weekLabel }}</span>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="week-nav-btn"
            aria-label="Next pay period"
            @click="$emit('shift-week', 1)"
          >
            <b-icon-chevron-right />
          </b-button>
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">Search</label>
        <b-form-input
          :value="searchQuery"
          placeholder="Project or worker name…"
          size="sm"
          class="filter-control filter-control-search"
          @input="$emit('update:searchQuery', $event)"
        />
      </div>

      <div class="filter-field filter-field-action">
        <label class="filter-label">Filters</label>
        <b-dropdown
          :variant="activeFilterCount ? 'primary' : 'outline-secondary'"
          size="sm"
          right
          menu-class="timesheet-filter-menu"
          class="filter-dropdown"
        >
          <template #button-content>
            <b-icon-funnel /> Filter
            <span v-if="activeFilterCount" class="filter-count">{{
              activeFilterCount
            }}</span>
          </template>
          <b-dropdown-form class="filter-dropdown-form" @click.stop>
            <p class="filter-menu-label">Status</p>
            <b-form-checkbox-group
              :checked="statusFilter"
              :options="statusFilterOptions"
              stacked
              @input="$emit('update:statusFilter', $event)"
            />
          </b-dropdown-form>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SupervisorTimesheetToolbar',

  props: {
    dateFrom: { type: String, default: '' },
    dateTo: { type: String, default: '' },
    selectedProjectId: { type: [Number, String], default: null },
    searchQuery: { type: String, default: '' },
    statusFilter: { type: Array, default: () => [] },
    weekLabel: { type: String, default: '-' },
    projectOptions: { type: Array, default: () => [] },
    statusFilterOptions: { type: Array, default: () => [] },
    activeFilterCount: { type: Number, default: 0 },
    canDownloadTimesheet: { type: Boolean, default: false },
    exportDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
    }
  },
  methods: {
    onFromInput(value) {
      this.$emit('update:dateFrom', value)
      this.$emit('date-from-change')
    },
  },
}
</script>

<style lang="scss" scoped>
.list-toolbar {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.toolbar-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.list-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}

.list-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-row {
  display: grid;
  grid-template-columns:
    minmax(132px, 0.75fr) minmax(132px, 0.75fr) minmax(180px, 1.1fr)
    max-content minmax(180px, 1fr) auto;
  align-items: flex-end;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.filter-field-action {
  white-space: nowrap;
}

.filter-field-date {
  min-width: 132px;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.date-input,
.filter-control {
  width: 100%;
}

/* b-form-datepicker's two-class .b-form-btn-label-control.form-control rule
   beats the app-wide one-class `.form-control` height on specificity, so it
   renders shorter than the sibling sm-sized filter controls. Match it back. */
/deep/ .date-input.form-control-sm {
  height: 34px;
  display: flex;
  align-items: center;
}

.week-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}

.week-nav-btn {
  width: 32px;
  padding-left: 0;
  padding-right: 0;
  flex-shrink: 0;
}

.week-nav-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
}

.filter-dropdown {
  width: 100%;
}

.filter-dropdown ::v-deep > .btn {
  min-width: 112px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1;
  background: rgba(255, 255, 255, 0.24);
  color: inherit;
}

::v-deep .timesheet-filter-menu {
  min-width: 220px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.16);
}

.filter-dropdown-form {
  padding: 4px 6px;
}

.filter-menu-label {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

@media (max-width: 575px) {
  .toolbar-actions {
    width: 100%;
    flex-wrap: nowrap;
  }

  .toolbar-actions .btn {
    flex: 1 1 0;
    padding: 0.3rem 0.5rem;
    font-size: 0.78rem;
    white-space: nowrap;
  }
}
</style>
